import { FC, ReactNode } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { CustomButton } from '@components';

const dialogPaperSx = {
  borderRadius: '28px',
  maxWidth: '800px',
  width: '800px',
};

type UserAccessDialogLayoutProps = {
  open: boolean;
  title: string;
  submitLabel: string;
  canSubmit: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
  children: ReactNode;
};

const UserAccessDialogLayout: FC<UserAccessDialogLayoutProps> = ({
  open,
  title,
  submitLabel,
  canSubmit,
  isSubmitting,
  onClose,
  onSubmit,
  children,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted={false}
      aria-labelledby="user-access-dialog-title"
      sx={{
        '& .MuiDialog-paper': {
          ...dialogPaperSx,
          ...(isMobile && { width: 'min(90vw, 400px)' }),
        },
      }}
    >
      <DialogTitle
        id="user-access-dialog-title"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          padding: '1.5rem',
          pb: '1rem',
          borderBottom: '1px solid #E8EAEB',
        }}
      >
        <Typography
          component="span"
          sx={{ fontSize: '1.375rem', fontWeight: 700, lineHeight: '2rem', color: '#162936' }}
        >
          {title}
        </Typography>
        <IconButton aria-label="close" onClick={onClose} disabled={isSubmitting} sx={{ p: 0 }}>
          <CloseOutlinedIcon sx={{ fontSize: '1.5rem', color: '#162936' }} />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          px: '1.5rem',
          pb: '4rem',
          mt: '1.5rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
        }}
      >
        {children}
      </DialogContent>
      <DialogActions
        sx={{
          padding: '1.5rem',
          pt: '1.25rem',
          gap: '0.5rem',
          borderTop: '1px solid #E8EAEB',
          boxShadow: '0px -1px 9px 0px #0000000F',
        }}
      >
        <CustomButton
          name="Cancel"
          onClick={onClose}
          disabled={isSubmitting}
          backgroundColor="#FFF"
          color="#51B8B0"
        />
        <CustomButton
          name={submitLabel}
          onClick={onSubmit}
          disabled={!canSubmit}
          backgroundColor="#2F736E"
          color="#FFF"
        />
      </DialogActions>
    </Dialog>
  );
};

export default UserAccessDialogLayout;
